import { Alert, Chip, ChipProps, Tooltip, Typography } from '@mui/material';
import { QualityClass, ScanStatus } from '../network';
import {
  Bolt,
  Cancel,
  CheckCircle,
  Report,
  WatchLater,
} from '@mui/icons-material';
import { FC, forwardRef } from 'react';

interface ScanStatusChipsProps {
  status: ScanStatus | null | undefined;
}

export const ScanStatusChip: FC<ScanStatusChipsProps> = ({ status }) => {
  if (status === 'Uploaded') {
    return <StatusChip label='New' icon={<Bolt />} color='secondary' />;
  }

  if (status === 'Processing') {
    return <StatusChip label='Processing' icon={<WatchLater />} color='info' />;
  }

  if (status === 'Partial') {
    return (
      <Tooltip title='One or more scans failed to process. Please review and rescan if needed'>
        <StatusChip
          label='Partial'
          icon={<Report />}
          color='warning'
          style={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  if (status === 'Failed') {
    return (
      <Tooltip title='Failed to process scan'>
        <StatusChip
          label='Failed'
          icon={<Cancel />}
          color='error'
          style={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  if (status === 'Completed') {
    return (
      <StatusChip
        label='Completed'
        title='Completed'
        icon={<CheckCircle />}
        color='success'
      />
    );
  }

  return null;
};

interface QualityClassProps {
  qualityClass: QualityClass | null | undefined;
}

export const QualityClassChip: FC<QualityClassProps> = ({ qualityClass }) => {
  if (qualityClass !== QualityClass.Warning) {
    return null;
  }

  return (
    <Tooltip
      title='Low-quality 3D model detected. AI predicted Cobb angle estimate may be inaccurate - please review and rescan if needed'
      arrow
    >
      <StatusChip
        label='Warning'
        icon={<Report />}
        color='warning'
        style={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
};

// need to use forwardRef to get the ref for tooltips to work
export const StatusChip = forwardRef<HTMLDivElement, ChipProps>(
  (props, ref) => {
    return (
      <Chip
        ref={ref}
        size='small'
        variant='outlined'
        {...props}
        sx={{
          '& .MuiChip-label': {
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            marginTop: '2px',
          },
        }}
      />
    );
  }
);

StatusChip.displayName = 'StatusChip';

export const QualityClassAlert: FC<QualityClassProps> = ({ qualityClass }) => {
  if (qualityClass !== QualityClass.Warning) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 8,
        right: 0,
        left: 0,
        padding: '8px 16px',
      }}
    >
      <Alert severity='warning'>
        <Typography variant='body2'>
          Low-quality 3D model detected. AI predicted Cobb angle estimate may be
          inaccurate - please review and rescan if needed
        </Typography>
      </Alert>
    </div>
  );
};
